import Link from 'next/link';
import React, { useMemo, useState, useContext, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { IconButton, Typography, Grid, Tooltip, TextField } from '@material-ui/core';
import { Dialog, DialogContent, DialogTitle, CardMedia, CircularProgress } from '@material-ui/core';
import { AppBar, Tabs, Tab, Button, Checkbox, Divider, Box, Select, MenuItem } from '@material-ui/core';
import { ToggleButtonGroup, ToggleButton } from '@material-ui/lab';
import CancelIcon from '@material-ui/icons/CancelSharp';
import CopyIcon from '@material-ui/icons/FileCopySharp';
import DownloadIcon from '@material-ui/icons/GetAppSharp';
import LockedIcon from '@material-ui/icons/LockSharp';
import UnlockedIcon from '@material-ui/icons/CloudDoneSharp';
import WarningIcon from '@material-ui/icons/ReportProblemOutlined';
import { makeStyles } from '@material-ui/core/styles';
import Carousel from 'react-material-ui-carousel';
import mixpanel from 'mixpanel-browser';

import { useAxios } from '../utils/axiosApi';
import { calculateMaterialsCostCredits } from '../utils/business';
import { useBakingApi, useMaterialsApi, useThumbnailUrls, previewMatFromMat, ScanType, getMaterialScanType } from '../utils/materialsLib';
import { useMetadataApi, makeSizeVals } from '../utils/metadataLib';
import { triggerUrl, getUrlFilename } from '../utils/htmlLib';
import { onError } from '../utils/errorLib';
import { usePolling } from '../utils/hooksLib';
import { AppContext } from './AppState';
import ConfirmModal from './ConfirmModal';
import LedgerTable from './LedgerTable';
import ProgressButton from './ProgressButton';

// Time in seconds between polling (e.g. to check baking processing completion)
const pollingInterval = 10;

// Bivot version to use in embed script.
const bivotVersion = 'v3.32.13';

const useStyles = makeStyles((theme) => ({
  modal: {
    minHeight: '450px',
    minWidth: '800px',
  },
  topBar: {
    height: '40px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'right',
    color: theme.palette.secondary.light,
    paddingRight: theme.spacing(2),
    background: theme.palette.secondary.main,
  },
  codeSnippet: {
    //padding: 10,  // Doesn't work here, have to set style directly.  Why?
    fontSize: '0.55em',
    color: '#777',
    lineHeight: 'normal',
    width: '285px',
  },
  closeButton: {
    float: 'right',
    color: 'grey',
    marginTop: theme.spacing(-1),
  },
  publishButton: {
    marginTop: theme.spacing(1),
    marginLeft: theme.spacing(0.5),
    marginRight: theme.spacing(0.5),
  },
  image: {
    height: 120,
    width: 210,
    marginBottom: theme.spacing(0.5),
  },
  downloadButton: {
    width: '140px',
    marginLeft: theme.spacing(0.5),
    marginRight: theme.spacing(0.5),
  },
  unlockButton: {
    display: 'flex',
    justifyContent: 'right',
    marginRight: theme.spacing(8.1),
  },
  copyButton: {
    width: '138px',
    marginTop: theme.spacing(1),
    marginLeft: theme.spacing(0.6),
    marginRight: theme.spacing(0.5),
    color: theme.palette.primary.main,
    backgroundColor: '#ffffff00',
    outlineWidth: '1px',
    outlineStyle: 'solid',
  },
  helpIcon: {
    marginTop: theme.spacing(2.5),
    marginLeft: theme.spacing(1),
  },
  progress: {
    marginTop: theme.spacing(1),
    marginLeft: theme.spacing(3.5),
  },
  sectionHeading: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(-1),
  },
  tabs: {
    marginBottom: '1em',
    backgroundColor: theme.palette.accent.main,
    boxShadow: 'none',
  },
  tab: {
    minWidth: '50px',
  },
  formatButton: {
    outlineWidth: '1px',
    outlineStyle: 'solid',
    marginLeft: theme.spacing(3),
    width: '125px',
  },
  formatButtonSmall: {
    outlineWidth: '1px',
    outlineStyle: 'solid',
    width: '68.5px',
    marginTop: theme.spacing(0.1),
    marginLeft: theme.spacing(0.6),
    marginRight: theme.spacing(0.25),
  },
  formatSelected: {
    color: theme.palette.primary.main,
    backgroundColor: '#ffffff00',
  },
  creditsText: {
    fontSize: '1.0em',
  },
  formatText: {
    fontSize: '0.9em',
  },
  sizeText: {
    fontSize: '0.8em',
  },
  sizeContainer: {
    width: '100%',
  },
  sizeGroup: {
    minHeight: '73px',
  },
  formatButtonGroup: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  downloadButtonGroup: {
    width: '100%',
    height: '40px',
    display: 'flex',
    alignItems: 'end',
    marginTop: theme.spacing(0.5),
    marginBottom: theme.spacing(1),
  },
  downloadContent: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    minHeight: '210px',
    paddingRight: theme.spacing(1),
  },
  shimmerContent: {
    paddingRight: theme.spacing(1),
  },
  checkboxGroup: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(-1.5),
    marginLeft: theme.spacing(1.4),
  },
  content: {
    marginTop: theme.spacing(-2),
    marginRight: theme.spacing(1),
    marginLeft: theme.spacing(1),
  },
  leftPanel: {
    marginRight: theme.spacing(2.5),
  },
  name: {
    fontSize: '1.1em',
    fontWeight: 'bold',
  },
  sku: {
    fontSize: '0.85em',
  },
  url: {
    paddingLeft: theme.spacing(2),
    fontSize: '0.8em',
    wordBreak: 'break-all',
  },
  shimmerText: {
    paddingLeft: theme.spacing(2),
    fontSize: '0.95em',
  },
  greyText: {
    color: '#777',
  },
  padLeft: {
    paddingLeft: theme.spacing(2),
  },
  alignCentre: {
    display: 'flex',
    alignItems: 'center',
  },
  alignTop: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'top',
  },
  textField: {
    marginTop: theme.spacing(-0.5),
  },
  ledger: {
    marginLeft: theme.spacing(2),
    width: '400px',
  },
  divider: {
    marginTop: theme.spacing(0.5),
    marginBottom: theme.spacing(0.5),
  },
  lockedText: {
    marginLeft: theme.spacing(2),
    marginRight: theme.spacing(9),
    marginTop: theme.spacing(-0.7),
    marginBottom: theme.spacing(2),
  },
  fadeOutHoverableDiv: {
    marginTop: theme.spacing(0.5),
    marginBottom: theme.spacing(0.5),
    zIndex: '1',
    '&:hover': {
      position: 'relative',
      flexWrap: 'wrap',
      overflow: 'visible',
      zIndex: '2',
    },
  },
  fadeOutHoverableSpan: {
    '&:hover': {
      display: 'inline-block',
      paddingRight: '40px !important',
      background: 'linear-gradient(270deg, ' +
        'rgba(250,250,250,0) 0%, ' +
        'rgba(250,250,250,1) 25px, ' +
        'rgba(250,250,250,1) 100%)',
    },
  },
  clickable: {
    cursor: 'pointer',
  },
  carouselContainer: {
    width: '100%',
    flexWrap: 'nowrap',
    marginTop: theme.spacing(1.5),
    marginBottom: theme.spacing(1.5),
  },
  carouselImageContainer: {
    width: '345px',
  },
  carouselLabel: {
    fontSize: '1.0em',
  },
  carouselImage: {
    height: '190px',
    width: '345px',
    marginTop: theme.spacing(0.5),
    marginBottom: theme.spacing(0.5),
  },
  carouselNavButtonWrapper: {
    '&:hover': {
      '& $carouselNavButton': {
        opacity: "0.8 !important"
      }
    },
  },
  carouselNavButton: {
    backgroundColor: `${theme.palette.primary.light} !important`,
    color: `${theme.palette.primary.darker} !important`,
  },
  outlinedBox: {
    border: '1px solid grey',
    padding: theme.spacing(0.5),
    cursor: 'pointer',
  },
  box: {
    border: '1px solid white',
    padding: theme.spacing(0.5),
    cursor: 'pointer',
  },
  boldMono: {
    fontWeight: 'bold',
    fontFamily: 'monospace',
    fontSize: '1em',
  },
  mono: {
    fontFamily: 'monospace',
    fontSize: '0.9em',
  },
  discountText: {
    color: theme.palette.primary.main,
    fontSize: '0.9em',
    fontWeight: 'bold',
  },
  colourPatch0: {
    width: '20px',
    height: '20px',
    backgroundColor: props => props.squareColors[0],
    marginRight: theme.spacing(1),
  },
  colourPatch1: {
    width: '20px',
    height: '20px',
    backgroundColor: props => props.squareColors[1],
    marginRight: theme.spacing(1),
  },
  colourPatch2: {
    width: '20px',
    height: '20px',
    backgroundColor: props => props.squareColors[2],
    marginRight: theme.spacing(1),
  },
  colourPatch3: {
    width: '20px',
    height: '20px',
    backgroundColor: props => props.squareColors[3],
    marginRight: theme.spacing(1),
  },
  colourPatch4: {
    width: '20px',
    height: '20px',
    backgroundColor: props => props.squareColors[4],
    marginRight: theme.spacing(1),
  },
}));

// Convert an origin URL to a cloudfront URL
function toCloudfrontUrl(url) {
  const parts = url.split('/');
  const slug = parts.slice(3).join('/');
  return `${process.env.NEXT_PUBLIC_PUBLISH_URL}/${slug}`
}


export default function ShareModal(props) {
  const { material, visible, onHide, unlock, onUnlock, publish, onPublish, render } = props;
  const { materialId, userId } = material ?? { };

  const { apiGet, apiPut, apiPost } = useAxios();
  const { getColourMaskUrls, getBillingCost } = useMaterialsApi();
  const { checkBakingStatus } = useBakingApi();
  const { fetchGalleryState } = useMetadataApi();

  const [currentColors, setCurrentColors] = useState([null, null, null, null, null]);
  const classes = useStyles({ squareColors: currentColors });
  const [tabValue, setTabValue] = useState(0);
  const [format, setFormat] = useState('PNG');
  const [untiled, setUntiled] = useState(false);
  const [preparingDownload, setPreparingDownload] = useState(false);
  const [preparingShimmerZip, setPreparingShimmerZip] = useState(false);
  const [preparingPublish, setPreparingPublish] = useState(false);
  const [preparingColourMasks, setPreparingColourMasks] = useState(false);
  const [embedString, setEmbedString] = useState('');
  const [shortCredits, setShortCredits] = useState(false);
  const [useCreditsConfirmation, setUseCreditsConfirmation] = useState(false);
  const [unlockCostCredits, setUnlockCostCredits] = useState();
  const [paidCredits, setPaidCredits] = useState();
  const [thumbnail, setThumbnail] = useState(null);
  const [carouselId, setCarouselId] = useState(0);
  const [autoplayHover, setAutoplayHover] = useState(true);
  const [autoplayClick, setAutoplayClick] = useState(true);
  const [colourMasks, setColourMasks] = useState(null);
  const [colourMasksMetadata, setColourMasksMetadata] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [activeMasks, setActiveMasks] = useState(null);
  const [codeToggle, setCodeToggle] = useState('React');
  const [tiledInfoVals, setTiledInfoVals] = useState({});
  const [untiledInfoVals, setUntiledInfoVals] = useState({});
  const {
    credits, setCredits,
    serviceCredits, setServiceCredits,
    galleryUser,
    showAlert,
  } = useContext(AppContext);

  const { user } = useAuth0();

  const [pollTime, setPollTime, pollsCompleted, setPollsCompleted] = usePolling(() => checkBakingStatus(userId, materialId));

  const unlocked = unlock;
  const published = publish ? publish.published : false;
  const embedPath = publish ? toCloudfrontUrl(publish['embedPath']) : null;
  const viewPath = publish ? toCloudfrontUrl(publish['viewPath']) : null;

  useEffect(() => {
    async function fetchMetadata(untiledVal) {
      if (untiledVal) {
        if (Object.keys(untiledInfoVals).length === 0) {
          const { state: untiledState } = await fetchGalleryState(material);
          setUntiledInfoVals(makeSizeVals(untiledState));
        }
      } else {
        if (Object.keys(tiledInfoVals).length === 0) {
          const previewMaterial = previewMatFromMat(material);
          if (previewMaterial) {
            const { state: tiledState } = await fetchGalleryState(previewMaterial);
            setTiledInfoVals(makeSizeVals(tiledState));
          }
        }
      }

      const serviced = (user.sub != galleryUser);
      const res = await getBillingCost(material.userId, material.materialId, serviced);
      setUnlockCostCredits(res);

      // Ensure Shimmer gets published if the material is already unlocked
      if (material.unlocked && !(material.publish ? material.publish.published : false)) {
        await doPublish(true, true);
      }
    }

    if (visible && material && user) {
      mixpanel.track('View Share Panel', { 'Material ID': material.materialId, 'Material Name': material.name });
      var untiledVal = untiled;
      if (material.texturesUserPath && !material.tiledUserPath) {
        untiledVal = true;
        setUntiled(untiledVal);
      }
      if (!material.texturesUserPath && !material.tiledUserPath) {
        setTabValue(1);
      } else {
        setTabValue(0);
      }
      fetchMetadata(untiledVal);
    }
  }, [visible, material, user, untiled]);

  useEffect(() => {
    async function startCheckAutoUnlock() {
      // The material appears to already be unlocked but the front end hasn't caught up yet.
      // This can happen to stitching inputs when the stitched material (and inputs) are unlocked,
      // or if the material has been unlocked in another window.
      // Confirm that the material is really unlocked by unlocking it for 0 credits.
      const res = await unlockWithCredits(true, unlockCostCredits, true);
    }

    if (!unlocked && unlockCostCredits && unlockCostCredits.credits === 0) {
      startCheckAutoUnlock();
    }
  }, [unlockCostCredits, unlocked]);

  const materialArray = useMemo(() => material ? [material] : [], [material]);
  const { thumbnailUrls } = useThumbnailUrls(material?.userId, materialArray, 'ShareModal');
  const thumbnailUrl = thumbnailUrls?.[material?.materialId];

  useEffect(() => {
    setThumbnail(thumbnail => thumbnailUrl ?? thumbnail);
  }, [thumbnailUrl]);

  useEffect(() => {
    async function startGetColourMasks() {
      setColourMasks(null);
      if (!ignore) {
        const data = await getColourMaskUrls(material?.userId, material?.materialId);
        if (data) {
          const { urls, metadata } = data
          setColourMasks(urls);
          setColourMasksMetadata(metadata);
        }
      }
    }

    let ignore = false;
    if (visible) {
      startGetColourMasks();
    }

    return () => { ignore = true; }
  }, [material, visible]);

  useEffect(() => {
    if (colourMasks && colourMasksMetadata && Object.keys(colourMasksMetadata).length > 0) {
      if (!activeId) {
        setActiveId(2);  // Initial number of colours
      } else {
        const { colors } = colourMasksMetadata[activeId];
        setCurrentColors(colors);
        var items = []
        colors.forEach((c, i) => {
          const obj = {
            name: `Mask ${i + 1} / ${colors.length}`,
            colorString: c,
            img: colourMasks[`${activeId}_${i}`]
          }
          items.push(obj);
        });
     }
    }
    setActiveMasks(items);
  }, [colourMasks, colourMasksMetadata, activeId])

  useEffect(() => {
    // Any time the share modal is opened, activate polling for texture baking.  This
    // will activate baking if required and not already underway.  Polling will stop
    // (pollTime will be reset to 0) once baking is ready.
    if (visible && unlocked) {
      setPollTime(pollingInterval);
    }
    // Reset polling state if hidden
    if (!visible && unlocked) {
      setPollTime(0);
      setPollsCompleted(0);
    }
  }, [visible, unlocked]);

  useEffect(() => {
    async function onUpdateEmbed() {
      if (embedPath && codeToggle) {
        setEmbedString(generateEmbedString(embedPath, codeToggle));
      }
    }
    onUpdateEmbed();
  }, [embedPath, codeToggle]);

  const useServiceCredits = user && (user.sub != galleryUser);

  function handleTabChange(_event, newValue) {
    setTabValue(parseInt(newValue));
    setAutoplayClick(true);
  }

  function handleSelectUntiled(event) {
    setUntiled(!untiled);
  }

  function generateEmbedString(path, codeType) {
    var str = '';
    if (path) {
      const embedJson = path.replace('gallery-embed.html', 'material-set.json');
      if (codeType == 'JS') {
        const distPath = `https://cdn.jsdelivr.net/gh/bandicoot-imaging-sciences/bivot@${bivotVersion}/src/bivot-js/dist`;
        str = `<canvas id="bivot-canvas"></canvas><script type="module">
  const bivSrc = '${distPath}/index.js';
  const materialSet = '${embedJson}';
  import(bivSrc).then(module => {const bivot = new module.newBivot({materialSet}); bivot.checkWebGL(); bivot.startRender();});
</script>`
      } else if (codeType == 'React') {
        str = `<Bivot materialSet='${embedJson}' />`;
      }
    }
    return str;
  }

  async function doPublish(skipAlert=false, forceTrue=false) {
    //console.log('doPublish()');
    var success = false;
    setPreparingPublish(true);

    const isPublished = forceTrue ? true : (publish ? publish['published'] : false);
    var response = {};
    try {
      response = await apiPost('/publish', { userId, materialId, update: isPublished }, {});
    } catch (e) {
      onError(e);
    }
    const { status, data } = response.data;
    if (status == 'success') {
      // Update local material cache
      onPublish({
        published: true,
        viewPath: data.publishedViewPath,
        embedPath: data.publishedEmbedPath
      });
      success = true;
      if (!skipAlert) {
        showAlert({ status: 'success', message: 'Material successfully unlocked' });
      }
    } else {
      showAlert({ status: 'error', message: 'An error occurred while publishing the material' });
    }

    setPreparingPublish(false);
    return success;
  }

  async function downloadTextures() {
    var success = false;
    setPreparingDownload(true);
    mixpanel.track('Download textures', { 'format': format, 'untiled': untiled, 'Material ID': material?.materialId, 'Material Name': material?.name });

    var params = {
      userId,
      materialId,
      outputType: untiled ? 'textures' : 'tiled'
    };
    if (format === 'GLTF') {
      params['format'] = 'GLTF';
    }

    var response = {};
    try {
      response = await apiGet('/publish', { params });
    } catch (e) {
      onError(e);
    }
    const { status, data } = response.data;
    var filename = getUrlFilename(data.zipFile);
    if (format === 'U3M' && filename.endsWith('.zip')) {
      filename = filename.slice(0, -4) + '.u3ma';
    }
    filename = filename.replace(' ', '_');
    if (status == 'success') {
      triggerUrl(data.zipFile, () => setPreparingDownload(false), filename);
      success = true;
    } else {
      showAlert({ status: 'error', message: 'An error occurred while preparing the Texture files for download' });
    }

    return success;
  }

  async function getShimmerZipFile() {
    //console.log('getShimmerZipFile()');
    var success = false;
    setPreparingShimmerZip(true);
    mixpanel.track('Download Shimmer', { 'Material ID': material?.materialId, 'Material Name': material?.name });

    var response = {};
    try {
      response = await apiGet('/publish', {
        params: { userId, materialId, outputType: 'shimmer' }
      });
    } catch (e) {
      onError(e);
    }
    const { status, data } = response.data;
    if (status == 'success') {
      triggerUrl(toCloudfrontUrl(data.zipFile), () => setPreparingShimmerZip(false));
      success = true;
    } else {
      showAlert({ status: 'error', message: 'An error occurred while preparing the Shimmer files for download' });
    }

    return success;
  }

 async function getColourMasks() {
  var success = false;
  setPreparingColourMasks(true);
  mixpanel.track('Download colour masks', { 'Material ID': material?.materialId, 'Material Name': material?.name, 'Num masks': activeId });

  var response = {};
  try {
    response = await apiGet('/publish', {
      params: { userId, materialId, outputType: 'masks', numMasks: activeId }
    });
  } catch (e) {
    onError(e);
  }
  const { status, data } = response.data;
  if (status == 'success') {
    triggerUrl(toCloudfrontUrl(data.zipFile), () => setPreparingColourMasks(false));
    success = true;
  } else {
    showAlert({ status: 'error', message: 'An error occurred while preparing the colour mask files for download' });
  }
 }

  async function tryUnlock() {
    mixpanel.track('Clicked Unlock Material', { 'Material ID': material?.materialId, 'Material Name': material?.name });
    const creditsToUse = useServiceCredits ? serviceCredits : credits;
    if (unlockCostCredits) {
      setUseCreditsConfirmation(true);
      setShortCredits(creditsToUse < unlockCostCredits.credits);
    }
  }

  async function handleConfirmUseCredits(confirm) {
    if (confirm) {
      const res = await unlockWithCredits(true, unlockCostCredits);
    }
    setUseCreditsConfirmation(false);
    setPaidCredits(null);
  }

  async function unlockWithCredits(alsoPublish, costObject, skipAlert=false) {
    var success = false;
    mixpanel.track('Unlock Material', {
      'Material ID': material.materialId,
      'Material Name': material.name,
      'Credits': !useServiceCredits && costObject?.credits || 0,
      'Service Credits': useServiceCredits && costObject?.credits || 0,
      'Scan Type': getMaterialScanType(material),
    });
    if (costObject) {
      var unlockMats = [material.materialId];
      if (!useServiceCredits) {
        // Unlocking a stitched material means also unlocking its inputs, unless using serviced credits
        costObject.stitchInputs.forEach(s => {
          if (!s.unlocked) {
            unlockMats.push(s.mid);
          }
        });
      }
      var body = { unlockMats };
      if (useServiceCredits) {
        body['useServiceCredits'] = costObject.credits;
      } else {
        body['useCredits'] = costObject.credits;
      }

      var response = {};
      try {
        response = await apiPut(
          '/billing',
          body,
          { params: { userId } },
        );
      } catch (e) {
        onError(e);
      }
      if (response?.data) {
        const { status, data } = response.data;

        if (status === 'success') {
          setPaidCredits(costObject.credits);
          onUnlock();
          if (data.newCreditBalance >= 0) {
            setCredits(data.newCreditBalance);
          }
          if (data.newServiceCreditBalance >= 0) {
            setServiceCredits(data.newServiceCreditBalance);
          }
          if (!skipAlert) {
            showAlert({ status: 'success', message: 'Material successfully unlocked' });
          }
          success = true;
          if (alsoPublish) {
            await doPublish(true);
          }
        }
      }
    }
    if (!success && !skipAlert) {
      showAlert({ status: 'error', message: 'An error occurred while unlocking the material' });
    }
    return success;
  }

  async function copySnippet(text, snippetType) {
    if (snippetType === 'link') {
      mixpanel.track('Copy Link', { 'Material ID': material?.materialId, 'Material Name': material?.name });
    } else if (snippetType === 'embed') {
      mixpanel.track('Copy Embed Code', { 'Material ID': material?.materialId, 'Material Name': material?.name, 'Code Type': codeToggle });
    }
    navigator.clipboard.writeText(text).then(function() {
      showAlert({ status: 'success', message: 'Copied to clipboard', duration: 3000 });
    }, function() {
      showAlert({ status: 'error', message: 'Copy to clipboard failed' });
    });
  }

  if (!material) {
    // In some situations ShareModal is mounted but hidden while there is no material property set. To avoid
    // errors on missing material properties and avoid expensive JSX renders we return null here.
    return null;
  }

  var imageCode = '';
  if (thumbnail) {
    imageCode =
      <CardMedia
        className={classes.image}
        image={thumbnail}
      />;
  }

  function makeCreditString() {
    if (paidCredits || unlockCostCredits) {
      const creditsDisplay = paidCredits ?? unlockCostCredits.credits;
      return `${creditsDisplay} ${(useServiceCredits ? 'service' : '')} credit${creditsDisplay !== 1 ? 's' : ''}`;
    } else {
      return '';
    }
  }

  function renderSizeVals(vals) {
    if (vals && (vals.mmX || vals.mmY || vals.pixX || vals.pixY || vals.dpi)) {
      return (
        <Grid container>
          <Grid item xs={12}>
            <Typography variant='caption' className={classes.sizeText}>
              {vals.mmX} x {vals.mmY} mm
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant='caption' className={classes.sizeText}>
              {vals.pixX} x {vals.pixY} px
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant='caption' className={classes.sizeText}>
              {vals.dpi} DPI
            </Typography>
          </Grid>
        </Grid>
      );
    } else {
      return '';
    }
  }

  const creditNotice = useServiceCredits ?
    <Typography variant='caption'>
      Service credits in account: <b>{serviceCredits}</b>
    </Typography>
    :
    <Typography variant='caption'>
      Credits in account: <b>{credits}</b>
    </Typography>;

  var ledgerColumns;
  var ledgerData = [];
  var stitchInputs = 0;
  var scanInputs = 0;
  var importInputs = 0;
  if (unlockCostCredits) {
    stitchInputs = unlockCostCredits.stitchInputs.length;
    scanInputs = unlockCostCredits.stitchInputs.filter(si => !si.import).length;
    importInputs = unlockCostCredits.stitchInputs.filter(si => si.import).length;
  }
  if (useServiceCredits) {
    ledgerColumns = ['Item', 'Count', 'Service credits'];
    ledgerData.push(['Serviced scan', 1, 1, 1]);
  } else if (stitchInputs === 0) {
    ledgerColumns = ['Item', 'Count', 'Credits'];
    if (material?.scanType === ScanType.TEXTURE_IMPORT) {
      ledgerData.push([<><div>Texture import</div><div className={classes.discountText}>Launch discount: 40% off</div></>, 1, calculateMaterialsCostCredits(0, 1), 2]);
    } else {
      ledgerData.push(['Processing - scan', 1, calculateMaterialsCostCredits(1), 1]);
    }
  } else {
    const scansAlreadyUnlocked = unlockCostCredits.stitchInputs.filter(si => (si.unlocked && !si.import)).length;
    const importsAlreadyUnlocked = unlockCostCredits.stitchInputs.filter(si => (si.unlocked && si.import)).length;
    const costScans = calculateMaterialsCostCredits(scanInputs);
    const costScansUnlocked = -calculateMaterialsCostCredits(scansAlreadyUnlocked);
    const costImports = calculateMaterialsCostCredits(0, importInputs);
    const costImportsUnlocked = -calculateMaterialsCostCredits(0, importsAlreadyUnlocked);
    ledgerColumns = ['Item', 'Count', 'Credits'];
    ledgerData.push(['Processing - stitching', 1, calculateMaterialsCostCredits(1), 1]);
    if (scanInputs > 0) {
      ledgerData.push(['Processing - stitch input scans', scanInputs, costScans, 1]);
    }
    if (scansAlreadyUnlocked > 0) {
      ledgerData.push(['Input scans already unlocked', scansAlreadyUnlocked, costScansUnlocked, 1]);
    }
    if (importInputs > 0) {
      ledgerData.push(['Texture import - stitch inputs', importInputs, costImports, 1]);
    }
    if (importsAlreadyUnlocked > 0) {
      ledgerData.push(['Texture imports already unlocked', importsAlreadyUnlocked, costImportsUnlocked, 1]);
    }
  }
  const creditBreakdown = (
    <LedgerTable
      columns={ledgerColumns}
      data={ledgerData}
      showTotal={2}
      className={classes.ledger}
    />
  );

  // For purchasing and publishing a material
  const unlockButton = (
    <Grid container>
      <Grid item xs={12} className={classes.unlockButton}>
        <ProgressButton
          className={classes.publishButton}
          color='primary'
          variant='contained'
          disabled={preparingPublish}
          onClick={tryUnlock}
          loading={preparingPublish}
          type='submit'
          success={false}
        >
          Unlock ({makeCreditString()})
        </ProgressButton>
      </Grid>
    </Grid>
  );

  const statusText = (<>
    <Grid item xs={12}>
      {!unlocked && (<>
        <Grid container>
          <Grid item className={classes.lockedText}>
            <Typography variant='caption' className={classes.formatText}>
              This material has not yet been purchased.
              Unlock to download the textures or share the material.
            </Typography>
          </Grid>
        </Grid>
        {(unlockCostCredits && unlockCostCredits.credits > 0) ? (<>
          <Grid container spacing={1}>
            <Grid item>
              {creditBreakdown}
            </Grid>
            <Grid item xs={12}>
              {unlockButton}
            </Grid>
          </Grid>
        </>) : (
          <CircularProgress size={24} className={classes.progress} />
        )}
      </>)}
    </Grid>
  </>);

  return (<>
    <Dialog classes={{paper: classes.modal}} centered='true' open={visible} onClose={onHide}>
      <div className={classes.topBar}>
        {!unlocked && creditNotice}
      </div>
      <DialogTitle>
        {unlocked && ('Material Download')}
        {!unlocked && ('Unlock')}
        <IconButton onClick={onHide} className={classes.closeButton}>
          <CancelIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent className={classes.content}>
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <div className={classes.leftPanel}>
              {imageCode}
              {render}
              <Grid container spacing={0}>
                <Grid item xs={12}>
                  <Typography noWrap variant='h4' className={`${classes.name} ${classes.fadeOutHoverableDiv}`}>
                    <span className={classes.fadeOutHoverableSpan}>
                      {material.name}
                    </span>
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography noWrap className={`${classes.sku} ${classes.fadeOutHoverableDiv}`}>
                    <span className={classes.fadeOutHoverableSpan}>
                      {material.sku ?? <span>&nbsp;</span>}
                    </span>
                  </Typography>
                </Grid>
                <Grid item xs={12} className={classes.sizeGroup}>
                  {tabValue === 0 && (<>
                    <Divider className={classes.divider} />
                    <Grid container wrap='nowrap' spacing={1} justifyContent='space-between' className={classes.sizeContainer}>
                      <Grid item xs={6} className={`${classes.padLeft}`}>
                        <Typography variant='caption' className={classes.sizeText}>
                          {untiled ? 'Untiled textures' : 'Tiled textures'}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        {renderSizeVals(untiled ? untiledInfoVals : tiledInfoVals)}
                      </Grid>
                    </Grid>
                  </>)}
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Divider className={classes.divider} />
                <Grid container spacing={1} className={`${classes.alignCentre} ${classes.greyText}`}>
                  <Grid item>
                    {unlocked ? <UnlockedIcon fontSize='small' /> : <LockedIcon fontSize='small' />}
                  </Grid>
                  <Grid item>
                    <Typography variant='caption'>
                      {unlocked ? 'Unlocked' : 'Not yet unlocked'}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
            </div>
          </Grid>
          {unlocked ? (
            <Grid item xs={8}>
              <AppBar position='static' className={classes.tabs}>
                <Tabs
                  classes={{
                    indicator: classes.indicator,
                  }}
                  value={tabValue}
                  onChange={handleTabChange}
                  indicatorColor='secondary'
                  textColor='secondary'
                  variant='fullWidth'
                >
                  <Tooltip title='Download textures for 3D workflows'>
                    <Tab
                      className={classes.tab}
                      label='Textures'
                    />
                  </Tooltip>
                  {activeId && (
                    <Tooltip title='Download colour masks for the material'>
                      <Tab
                        className={classes.tab}
                        label='Colour masks'
                      />
                    </Tooltip>
                  )}
                  <Tooltip title='Share or embed a Shimmer View of the material'>
                    <Tab
                      className={classes.tab}
                      label='Shimmer View'
                    />
                  </Tooltip>
                </Tabs>
              </AppBar>
              {tabValue === 0 && (<>
                {(material.texturesUserPath || material.tiledUserPath) ? (<>
                  <Grid container className={classes.downloadContent}>
                    <Grid item xs={12}>
                      <Grid container className={`${classes.formatButtonGroup}`} spacing={2}>
                        <Grid item xs={5}>
                          <Button
                            className={`${classes.formatButton} ${format === 'PNG' && classes.formatSelected}`}
                            onClick={() => setFormat('PNG')}
                          >
                            PNG
                          </Button>
                        </Grid>
                        <Grid item xs={7} className={`${classes.formatText} ${format === 'PNG' && classes.formatSelected}`}>
                          Universal format (for V-Ray, Substance, Blender, and others)
                        </Grid>
                        {material.formatsPresent && material.formatsPresent[untiled ? 1 : 0].includes('GLTF') && (<>
                          <Grid item xs={5}>
                            <Button
                              className={`${classes.formatButton} ${format === 'GLTF' && classes.formatSelected}`}
                              onClick={() => setFormat('GLTF')}
                            >
                              GLTF
                            </Button>
                          </Grid>
                          <Grid item xs={7} className={`${classes.formatText} ${format === 'GLTF' && classes.formatSelected}`}>
                            Best format for CLO3D
                          </Grid>
                        </>)}
                        <Grid item xs={5}>
                          <Button
                            className={`${classes.formatButton} ${format === 'U3M' && classes.formatSelected}`}
                            onClick={() => setFormat('U3M')}
                          >
                            U3M
                          </Button>
                        </Grid>
                        <Grid item xs={7} className={`${classes.formatText} ${format === 'U3M' && classes.formatSelected}`}>
                          Best format for Browzwear & Optitex
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid item xs={12}>
                      <Grid container className={`${classes.downloadButtonGroup}`}>
                        <Grid item xs={8}>
                          {material.texturesUserPath && (
                            <Grid container className={classes.checkboxGroup}>
                              <Grid item>
                                <Checkbox className={classes.checkBox}
                                  checked={untiled}
                                  onClick={handleSelectUntiled}
                                  disabled={!material.tiledUserPath}
                                />
                              </Grid>
                              <Grid item>
                                <Typography
                                  className={`${classes.formatText} ${classes.clickable}`}
                                  onClick={handleSelectUntiled}
                                >
                                  Download untiled texture maps
                                </Typography>
                              </Grid>
                            </Grid>
                          )}
                        </Grid>
                        <Grid item xs={4}>
                          <ProgressButton
                            className={classes.downloadButton}
                            color='primary'
                            disabled={preparingDownload || pollTime !== 0}
                            onClick={downloadTextures}
                            loading={preparingDownload}
                            type='submit'
                            success={false}
                          >
                            Download &nbsp;
                            <DownloadIcon />
                          </ProgressButton>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                  {pollsCompleted > 0 && (<>
                    {(
                      pollTime !== 0 &&
                      (material.tiledUserPath || material.texturesUserPath)
                    ) && (
                      <Grid container spacing={1} className={classes.alignCentre}>
                        <Grid item xs={2}>
                          <CircularProgress className={classes.progress} size={18} />
                        </Grid>
                        <Grid item xs={10}>
                          <Typography variant='caption' className={classes.greyText}>
                            Updating textures with your changes.  Please wait a minute...
                          </Typography>
                        </Grid>
                      </Grid>
                    )}
                    {(
                      pollTime === 0 &&
                      (material.tiledUserPath || material.texturesUserPath) &&
                      material.texBaking?.result === 'fail'
                    ) && (
                      <Grid container spacing={1} className={classes.alignCentre}>
                        <Grid item xs={2}>
                          <WarningIcon className={`${classes.progress} ${classes.greyText}`} />
                        </Grid>
                        <Grid item xs={10} className={classes.greyText}>
                          <Typography variant='caption'><i>Couldn&apos;t update textures with your last saved settings.  We&apos;re working on it!</i></Typography>
                        </Grid>
                      </Grid>
                    )}
                  </>)}
                </>) : (
                  'No textures available for download'
                )}
              </>)}
              {tabValue === 1 && (<>
                {activeMasks && (<>
                  <Grid container
                    className={classes.carouselContainer}
                    onMouseEnter={() => setAutoplayHover(false)}
                    onMouseLeave={() => setAutoplayHover(true)}
                  >
                    <Grid item xs={9}>
                      <div className={classes.carouselImageContainer}>
                        <Carousel
                          index={carouselId}
                          onChange={(i) => setCarouselId(i)}
                          autoPlay={autoplayHover && autoplayClick}
                          fullHeightHover={true}
                          navButtonsProps={{ className: classes.carouselNavButton }}
                          navButtonsWrapperProps={{ className: classes.carouselNavButtonWrapper }}
                          indicators={false}
                        >
                          {activeMasks.map((item, i) => (
                            <CardMedia
                              key={i}
                              className={classes.carouselImage}
                              image={item.img}
                            />
                          ))}
                        </Carousel>
                      </div>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography variant='caption' className={classes.carouselLabel}>
                        {activeMasks[carouselId].name}
                      </Typography>
                      {Object.values(activeMasks).map((mask, i) =>
                        <div
                          key={i}
                          className={`${classes.alignCentre} ${carouselId === i ? classes.outlinedBox : classes.box}`}
                          onClick={() => { setCarouselId(i); setAutoplayClick(false); }}
                        >
                          <Box className={classes[`colourPatch${i}`]} />
                          <Typography variant='caption' className={carouselId === i ? classes.boldMono : classes.mono}>
                            {mask.colorString}
                          </Typography>
                        </div>
                      )}
                    </Grid>
                  </Grid>
                  <Grid container spacing={1} className={classes.alignCentre}>
                    <Grid item xs={5}>
                      <Typography>
                        Number of colour masks:
                      </Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Select
                        value={activeId}
                        onChange={(event) => { setActiveId(event.target.value); setCarouselId(0); setAutoplayClick(true); }}
                      >
                        {[2, 3, 4, 5].map((label) => (
                          <MenuItem value={label} key={label}>{label}</MenuItem>
                        ))}
                      </Select>
                    </Grid>
                    <Grid item xs={3}>
                      <ProgressButton
                        className={classes.downloadButton}
                        color='primary'
                        disabled={preparingShimmerZip}
                        onClick={getColourMasks}
                        loading={preparingColourMasks}
                        type='submit'
                        success={false}
                        tooltip={`Download the selected set of colour masks (${activeId} colours)`}
                      >
                        Download &nbsp;
                        <DownloadIcon />
                      </ProgressButton>
                    </Grid>
                  </Grid>
                </>)}
              </>)}
              {tabValue === (activeId ? 2 : 1) && (
                <Grid container spacing={2} className={classes.shimmerContent}>
                  {published ? (<>
                    <Grid item xs={4} className={classes.alignCentre}>
                      <ProgressButton
                        className={classes.downloadButton}
                        color='primary'
                        disabled={preparingShimmerZip}
                        onClick={getShimmerZipFile}
                        loading={preparingShimmerZip}
                        type='submit'
                        success={false}
                        tooltip='Download Shimmer view file bundle for self-hosting in your website'
                      >
                        Download &nbsp;
                        <DownloadIcon />
                      </ProgressButton>
                    </Grid>
                    <Grid item xs={8} className={classes.alignCentre}>
                      <Typography className={classes.shimmerText}>
                        Bundle with Shimmer view files
                      </Typography>
                    </Grid>

                    {viewPath && (<>
                      <Grid item xs={4} className={classes.alignCentre}>
                        <Tooltip title='Copy sharing link, anyone with this link may view the Shimmer'>
                          <Button
                            className={classes.copyButton}
                            onClick={() => copySnippet(viewPath, 'link')}
                            type='submit'
                          >
                            Copy link &nbsp;
                            <CopyIcon />
                          </Button>
                        </Tooltip>
                      </Grid>
                      <Grid item xs={8} className={classes.alignCentre}>
                        <Typography className={classes.url}>
                          <a href={viewPath} target='_blank'>{viewPath}</a><br />
                        </Typography>
                      </Grid>
                    </>)}

                    {embedPath && (<>
                      <Grid item xs={4} className={classes.alignTop}>
                        <Grid container>
                          <Grid item xs={12}>
                            <Tooltip title='Copy code snippet to embed the Shimmer in your website'>
                              <Button
                                className={classes.copyButton}
                                onClick={() => copySnippet(embedString, 'embed')}
                                type='submit'
                              >
                                Embed &nbsp;
                                <CopyIcon />
                              </Button>
                            </Tooltip>
                          </Grid>
                          <Grid item xs={12}>
                            <ToggleButtonGroup
                              value={codeToggle}
                              exclusive
                              size='small'
                              onChange={(_event, value) => setCodeToggle(value)}
                            >
                              <ToggleButton value='React' className={classes.formatButtonSmall}>
                                React
                              </ToggleButton>
                              <ToggleButton value='JS' className={classes.formatButtonSmall}>
                                JS
                              </ToggleButton>
                            </ToggleButtonGroup>
                          </Grid>
                        </Grid>
                      </Grid>
                      <Grid item xs={8} className={classes.alignTop}>
                        <TextField
                          className={classes.textField}
                          InputProps={{
                            classes: { input: classes.codeSnippet, },
                            style: { padding: 10 },
                            disableUnderline: true
                          }}
                          id='filled-secondary'
                          variant='standard'
                          color='secondary'
                          minRows={6}
                          multiline
                          value={embedString}
                        />
                      </Grid>
                    </>)}
                  </>) : (<>
                    Preparing Shimmer...
                  </>)}
                </Grid>
              )}
            </Grid>
          ) : (
            <Grid item xs={8}>
              <Grid container>
                {statusText}
              </Grid>
            </Grid>
          )}
        </Grid>
      </DialogContent>
    </Dialog>
    {unlockCostCredits && (
      <ConfirmModal
        visible={useCreditsConfirmation}
        title={<>Unlock Material</>}
        message={
          shortCredits ? (
            <Grid container>
              <Grid item xs={12} className={classes.sectionHeading}>
                <Typography>You need {unlockCostCredits.credits} credit{unlockCostCredits.credits === 1 ? '' : 's'} to unlock this material</Typography>
              </Grid>
              <Grid item xs={12}>
                <ul>
                  <li key='1'>
                    <Typography className={classes.creditsText}>
                      <Link href='/buy?p=credits'>Buy credits</Link> in the web app, or
                    </Typography>
                  </li>
                  <li key='2'>
                    <Typography className={classes.creditsText}>
                      <Link href='https://www.bandicootimaging.com/connect/solution/self-service'>Contact</Link> the Bandicoot sales team
                    </Typography>
                  </li>
                </ul>
              </Grid>
            </Grid>
          ) : (
            <>Spend {makeCreditString()} to unlock material: <b>{material.name}?</b></>
          )
        }
        onYes={() => handleConfirmUseCredits(true)}
        onNo={() => handleConfirmUseCredits(false)}
        confirmText='Unlock'
        yesDisabled={shortCredits}
      />
    )}
  </>);
}
